/**
 * Interprets the Flash Regions Table stored in flash.
 *
 * The micro:bit flash layout is divided in flash regions, each containing a
 * different type of data (Nordic SoftDevice, MicroPython, bootloader, etc).
 * One of the regions is dedicated to the micro:bit filesystem, and this info
 * is used by this library to add the user files into a MicroPython hex File.
 *
 * The Flash Regions Table stores a data table at the end of the last flash page
 * used by the MicroPython runtime.
 * The table contains a series of 16-byte rows with info about each region
 * and it ends with a 16-byte table header with info about the table itself.
 * All in little-endian format.
 *
 * ```
 * |                                                               | Low address
 * | ID| HT|1ST_PAG| REGION_LENGTH | HASH_DATA                     | Row 1
 * | ID| HT|1ST_PAG| REGION_LENGTH | HASH_DATA                     | ...
 * | ID| HT|1ST_PAG| REGION_LENGTH | HASH_DATA                     | Row N
 * | MAGIC_1       | VER   | T_LEN |REG_CNT| P_SIZE| MAGIC_2       | Header
 * |---|---|---|---|---|---|---|---|---|---|---|---|---|---|---|---| Page end
 * |0x0|0x1|0x2|0x3|0x4|0x5|0x6|0x7|0x8|0x9|0xa|0xb|0xc|0xd|0xe|0xf|
 * |---|---|---|---|---|---|---|---|---|---|---|---|---|---|---|---|
 * ```
 *
 * More information about how this data is added to the MicroPython Intel Hex
 * file can be found in the MicroPython for micro:bit v2 repository:
 *   https://github.com/microbit-foundation/micropython-microbit-v2/blob/v2.0.0-beta.3/src/addlayouttable.py
 *
 * @packageDocumentation
 *
 * (c) 2020 Micro:bit Educational Foundation and the microbit-fs contributors.
 * SPDX-License-Identifier: MIT
 */
import MemoryMap from 'nrf-intel-hex';
import { areUint8ArraysEqual } from './common';
import * as hexMapUtil from './hex-map-utils';
/** Indicates the data contain in each of the different regions */
var RegionId;
(function (RegionId) {
    /** Soft Device is the data blob containing the Nordic Bluetooth stack. */
    RegionId[RegionId["softDevice"] = 1] = "softDevice";
    /** Contains the MicroPython runtime. */
    RegionId[RegionId["microPython"] = 2] = "microPython";
    /** Contains the MicroPython microbit filesystem reserved flash. */
    RegionId[RegionId["fs"] = 3] = "fs";
})(RegionId || (RegionId = {}));
/**
 * The "hash type" field in a region row indicates how to interpret the "hash
 * data" field.
 */
var RegionHashType;
(function (RegionHashType) {
    /** The hash data is empty. */
    RegionHashType[RegionHashType["empty"] = 0] = "empty";
    /** The full hash data field is used as a hash of the region in flash */
    RegionHashType[RegionHashType["data"] = 1] = "data";
    /** The 4 LSB bytes of the hash data field are used as a pointer  */
    RegionHashType[RegionHashType["pointer"] = 2] = "pointer";
})(RegionHashType || (RegionHashType = {}));
// Sizes for each of the fields in the Flash Regions Table header
var MAGIC2_LEN_BYTES = 4;
var P_SIZE_LOG2_LEN_BYTES = 2;
var NUM_REG_LEN_BYTES = 2;
var TABLE_LEN_LEN_BYTES = 2;
var VERSION_LEN_BYTES = 2;
var MAGIC_1_LEN_BYTES = 4;
/**
 * Offset for each of the Table header fields, starting from the end of the row.
 *
 * These are the fields stored in each row for each of the regions, and
 * any additional region data from the Region interface is derived from this.
 *
 * |0x00|..|..|0x03|0x04|0x05|0x06|0x07|0x08|0x09|0x0a|0x0b|0x0c|..|..|0x0f|
 * |----|--|--|----|----|----|----|----|----|----|----|----|----|--|--|----|
 * | MAGIC_1       | VERSION |TABLE_LEN|REG_COUNT| P_SIZE  | MAGIC_2       |
 */
var RegionHeaderOffset;
(function (RegionHeaderOffset) {
    RegionHeaderOffset[RegionHeaderOffset["magic2"] = MAGIC2_LEN_BYTES] = "magic2";
    RegionHeaderOffset[RegionHeaderOffset["pageSizeLog2"] = RegionHeaderOffset.magic2 + P_SIZE_LOG2_LEN_BYTES] = "pageSizeLog2";
    RegionHeaderOffset[RegionHeaderOffset["regionCount"] = RegionHeaderOffset.pageSizeLog2 + NUM_REG_LEN_BYTES] = "regionCount";
    RegionHeaderOffset[RegionHeaderOffset["tableLength"] = RegionHeaderOffset.regionCount + TABLE_LEN_LEN_BYTES] = "tableLength";
    RegionHeaderOffset[RegionHeaderOffset["version"] = RegionHeaderOffset.tableLength + VERSION_LEN_BYTES] = "version";
    RegionHeaderOffset[RegionHeaderOffset["magic1"] = RegionHeaderOffset.version + MAGIC_1_LEN_BYTES] = "magic1";
})(RegionHeaderOffset || (RegionHeaderOffset = {}));
// Magic numbers to identify the Flash Regions Table in flash
var REGION_HEADER_MAGIC_1 = 0x597f30fe;
var REGION_HEADER_MAGIC_2 = 0xc1b1d79d;
// Sizes for each of the fields in each Region row from the Flash Regions Table
var REGION_ID_BYTES = 1;
var REGION_HASH_TYPE_BYTES = 1;
var REGION_START_PAGE_BYTES = 2;
var REGION_LEN_BYTES = 4;
var REGION_HASH_DATA_BYTES = 8;
/**
 * Offset for each of the Region row fields, starting from the end of the row.
 *
 * These are the fields stored in each row for each of the regions, and
 * any additional region data from the Region interface is derived from this.
 *
 * |0x00|0x01|0x02|0x03|0x04|0x05|0x06|0x07|0x08|..|..|..|..|..|..|0x0f|
 * |----|----|----|----|----|----|----|----|----|--|--|--|--|--|--|----|
 * | ID | HT |1ST_PAGE | REGION_LENGTH     | HASH_DATA                 |
 */
var RegionRowOffset;
(function (RegionRowOffset) {
    RegionRowOffset[RegionRowOffset["hashData"] = REGION_HASH_DATA_BYTES] = "hashData";
    RegionRowOffset[RegionRowOffset["lengthBytes"] = RegionRowOffset.hashData + REGION_LEN_BYTES] = "lengthBytes";
    RegionRowOffset[RegionRowOffset["startPage"] = RegionRowOffset.lengthBytes + REGION_START_PAGE_BYTES] = "startPage";
    RegionRowOffset[RegionRowOffset["hashType"] = RegionRowOffset.startPage + REGION_HASH_TYPE_BYTES] = "hashType";
    RegionRowOffset[RegionRowOffset["id"] = RegionRowOffset.hashType + REGION_ID_BYTES] = "id";
})(RegionRowOffset || (RegionRowOffset = {}));
var REGION_ROW_LEN_BYTES = RegionRowOffset.id;
/**
 * Iterates through the provided Intel Hex Memory Map and tries to find the
 * Flash Regions Table header, by looking for the magic values at the end of
 * each flash page.
 *
 * TODO: Indicate here what errors can be thrown.
 *
 * @param iHexMap - Intel Hex memory map to scan for the Flash Regions Table.
 * @param pSize - Flash page size to scan at the end of each page.
 * @returns The table header data.
 */
function getTableHeader(iHexMap, pSize) {
    if (pSize === void 0) { pSize = 1024; }
    var endAddress = 0;
    var magic1ToFind = new Uint8Array(new Uint32Array([REGION_HEADER_MAGIC_1]).buffer);
    var magic2ToFind = new Uint8Array(new Uint32Array([REGION_HEADER_MAGIC_2]).buffer);
    var mapEntries = iHexMap.paginate(pSize, 0xff).entries();
    for (var iter = mapEntries.next(); !iter.done; iter = mapEntries.next()) {
        if (!iter.value)
            continue;
        var blockByteArray = iter.value[1];
        var subArrayMagic2 = blockByteArray.subarray(-RegionHeaderOffset.magic2);
        if (areUint8ArraysEqual(subArrayMagic2, magic2ToFind) &&
            areUint8ArraysEqual(blockByteArray.subarray(-RegionHeaderOffset.magic1, -(RegionHeaderOffset.magic1 - MAGIC_1_LEN_BYTES)), magic1ToFind)) {
            var pageStartAddress = iter.value[0];
            endAddress = pageStartAddress + pSize;
            break;
        }
    }
    // TODO: Throw an error if table is not found.
    var version = hexMapUtil.getUint16(iHexMap, endAddress - RegionHeaderOffset.version);
    var tableLength = hexMapUtil.getUint16(iHexMap, endAddress - RegionHeaderOffset.tableLength);
    var regionCount = hexMapUtil.getUint16(iHexMap, endAddress - RegionHeaderOffset.regionCount);
    var pageSizeLog2 = hexMapUtil.getUint16(iHexMap, endAddress - RegionHeaderOffset.pageSizeLog2);
    var pageSize = Math.pow(2, pageSizeLog2);
    var startAddress = endAddress - RegionHeaderOffset.magic1;
    return {
        pageSizeLog2: pageSizeLog2,
        pageSize: pageSize,
        regionCount: regionCount,
        tableLength: tableLength,
        version: version,
        endAddress: endAddress,
        startAddress: startAddress,
    };
}
/**
 * Parses a Region rows from a Flash Regions Table inside the Intel Hex memory
 * map, which ends at the provided rowEndAddress.
 *
 * Since the Flash Regions Table is placed at the end of a page, we iterate
 * from the end to the beginning.
 *
 * @param iHexMap - Intel Hex memory map to scan for the Flash Regions Table.
 * @param rowEndAddress - Address at which the row ends (same as the address
 *    where the next row or table header starts).
 * @returns The Region info from the row.
 */
function getRegionRow(iHexMap, rowEndAddress) {
    var id = hexMapUtil.getUint8(iHexMap, rowEndAddress - RegionRowOffset.id);
    var hashType = hexMapUtil.getUint8(iHexMap, rowEndAddress - RegionRowOffset.hashType);
    var hashData = hexMapUtil.getUint64(iHexMap, rowEndAddress - RegionRowOffset.hashData);
    var hashPointerData = '';
    if (hashType === RegionHashType.pointer) {
        // Pointer to a string in the hex is only 4 bytes instead of 8
        hashPointerData = hexMapUtil.getString(iHexMap, hashData & 0xffffffff);
    }
    var startPage = hexMapUtil.getUint16(iHexMap, rowEndAddress - RegionRowOffset.startPage);
    var lengthBytes = hexMapUtil.getUint32(iHexMap, rowEndAddress - RegionRowOffset.lengthBytes);
    return {
        id: id,
        startPage: startPage,
        lengthBytes: lengthBytes,
        hashType: hashType,
        hashData: hashData,
        hashPointerData: hashPointerData,
    };
}
/**
 * Reads the Flash Regions Table data from an Intel Hex map and retrieves the
 * MicroPython DeviceMemInfo data.
 *
 * @throws {Error} When the Magic Header is not present.
 * @throws {Error} When the MicroPython or FS regions are not found.
 *
 * @param intelHexMap - Memory map of the Intel Hex to scan.
 * @returns Object with the parsed data from the Flash Regions Table.
 */
function getHexMapFlashRegionsData(iHexMap) {
    // TODO: There is currently have some "internal" knowledge here and it's
    // scanning the flash knowing the page size is 4 KBs
    var tableHeader = getTableHeader(iHexMap, 4096);
    var regionRows = {};
    for (var i = 0; i < tableHeader.regionCount; i++) {
        var rowEndAddress = tableHeader.startAddress - i * REGION_ROW_LEN_BYTES;
        var regionRow = getRegionRow(iHexMap, rowEndAddress);
        regionRows[regionRow.id] = regionRow;
    }
    if (!regionRows.hasOwnProperty(RegionId.microPython)) {
        throw new Error('Could not find a MicroPython region in the regions table.');
    }
    if (!regionRows.hasOwnProperty(RegionId.fs)) {
        throw new Error('Could not find a File System region in the regions table.');
    }
    // Have to manually set the start at address 0 even if regions don't cover it
    var runtimeStartAddress = 0;
    var runtimeEndAddress = regionRows[RegionId.microPython].startPage * tableHeader.pageSize +
        regionRows[RegionId.microPython].lengthBytes;
    // The table is placed at the end of the last page used by MicroPython and we
    // need to include it
    runtimeEndAddress = tableHeader.endAddress;
    var uPyVersion = regionRows[RegionId.microPython].hashPointerData;
    var fsStartAddress = regionRows[RegionId.fs].startPage * tableHeader.pageSize;
    var fsEndAddress = fsStartAddress + regionRows[RegionId.fs].lengthBytes;
    return {
        flashPageSize: tableHeader.pageSize,
        flashSize: 512 * 1024,
        flashStartAddress: 0,
        flashEndAddress: 512 * 1024,
        runtimeStartAddress: runtimeStartAddress,
        runtimeEndAddress: runtimeEndAddress,
        fsStartAddress: fsStartAddress,
        fsEndAddress: fsEndAddress,
        uPyVersion: uPyVersion,
        deviceVersion: "V2" /* V2 */,
    };
}
/**
 * Reads the Flash Regions Table data from an Intel Hex map and retrieves the
 * MicroPython DeviceMemInfo data.
 *
 * @throws {Error} When the Magic Header is not present.
 * @throws {Error} When the MicroPython or FS regions are not found.
 *
 * @param intelHex - MicroPython Intel Hex string.
 * @returns Object with the parsed data from the Flash Regions Table.
 */
function getIntelHexFlashRegionsData(intelHex) {
    return getHexMapFlashRegionsData(MemoryMap.fromHex(intelHex));
}
export { getHexMapFlashRegionsData, getIntelHexFlashRegionsData };
//# sourceMappingURL=flash-regions.js.map